import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import { ClipLoader } from "react-spinners";
import { FiMapPin, FiClock, FiCheck, FiUsers, FiHeart, FiTrendingUp } from "react-icons/fi";

// GuestLinkup theme: navy (--primary: 55,55,88) and green (--secondary: 28,173,112)
// Matches the palette/approach used on the Help Center page.
const NAVY = "#373758";
const GREEN = "#1cad70";

const APPLY_EMAIL = "sukhbir@guestlinkup.com";

const VALUES = [
  {
    icon: FiUsers,
    bg: "#eaf6f0",
    fg: GREEN,
    title: "Real impact, not just a job title",
    text: "We're a handful of people, not a hundred. What you build ships, and the homestay owners on our platform actually feel it.",
  },
  {
    icon: FiHeart,
    bg: "#eef0f7",
    fg: NAVY,
    title: "Work that matters to a place",
    text: "Every homestay we onboard is a family business in a region tourism has often overlooked. You're not just growing a company.",
  },
  {
    icon: FiTrendingUp,
    bg: "#fdf2e9",
    fg: "#c98a2e",
    title: "Room to grow with us",
    text: "We're early-stage. The person who takes this on today will have a very different, bigger role a year from now.",
  },
];

// Postings are admin-managed (Admin -> Job Postings) and stored as one bullet
// per line, so split on newlines to render as checklists.
const toList = (text) =>
  (text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const Careersection = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}career/jobs`);
        setJobs(res.data.data || []);
      } catch (err) {
        console.error("Failed to load job postings", err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh", width: "100%" }}>
        <ClipLoader size={45} />
      </div>
    );
  }

  return (
    <>
      <div className="innerbanner"></div>

      {/* HERO */}
      <section className="py-5" style={{ background: "linear-gradient(180deg, #eaf6f0 0%, #f6fbfb 100%)" }}>
        <div className="container text-center">
          <div className="text-uppercase fw-bold small mb-2" style={{ color: GREEN, letterSpacing: "0.14em" }}>
            Careers at GuestLinkup
          </div>
          <h1 className="fw-bold mb-3" style={{ color: NAVY, fontSize: "2.4rem" }}>
            Small Team. Real Homestays.<br />
            <span style={{ color: GREEN }}>Work That Shows.</span>
          </h1>
          <p className="mx-auto text-muted" style={{ maxWidth: 620, fontSize: "16px", lineHeight: 1.75 }}>
            GuestLinkup is a small team building the platform that connects travelers with real,
            family-run homestays across Sikkim, Darjeeling, and the wider Northeast. We're early,
            and every person who joins us shapes what this becomes.
          </p>
        </div>
      </section>

      {/* WHY WORK HERE + OPEN POSITIONS */}
      <section className="py-5" style={{ background: "#fff" }}>
        <div className="container">
          <div className="row g-4 mb-5">
            {VALUES.map(({ icon: Icon, bg, fg, title, text }) => (
              <div className="col-lg-4 col-md-6" key={title}>
                <div
                  className="h-100 shadow-sm"
                  style={{ background: "#fff", border: "1px solid #eef0ee", borderRadius: 16, overflow: "hidden" }}
                >
                  <div className="d-flex align-items-center justify-content-center" style={{ height: 100, background: bg }}>
                    <Icon size={30} style={{ color: fg }} />
                  </div>
                  <div className="p-4">
                    <h6 className="fw-bold mb-2" style={{ color: NAVY }}>{title}</h6>
                    <p className="text-muted small mb-0">{text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <h4 className="fw-bold mb-1" style={{ color: NAVY }}>Open Positions</h4>
          <p className="text-muted mb-4">
            {jobs.length > 0
              ? `${jobs.length} role${jobs.length > 1 ? "s" : ""} we're actively hiring for right now.`
              : "No open roles right now — check back soon."}
          </p>

          {jobs.map((job) => {
            const mailtoHref = `mailto:${APPLY_EMAIL}?subject=${encodeURIComponent(
              `Application: ${job.title}`
            )}`;
            const responsibilities = toList(job.responsibilities);
            const requirements = toList(job.requirements);

            return (
              <div
                key={job.id}
                className="shadow-sm mb-4"
                style={{ background: "#fff", border: "1px solid #eef0ee", borderRadius: 16, padding: "32px 36px" }}
              >
                <h5 className="fw-bold mb-2" style={{ color: NAVY, fontSize: "1.3rem" }}>{job.title}</h5>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <span className="d-inline-flex align-items-center small text-muted fw-semibold">
                    <FiMapPin size={14} style={{ color: GREEN, marginRight: 6 }} /> {job.location}
                  </span>
                  <span className="d-inline-flex align-items-center small text-muted fw-semibold">
                    <FiClock size={14} style={{ color: GREEN, marginRight: 6 }} /> {job.job_type}
                  </span>
                </div>
                <p className="mb-4" style={{ color: "#5C5C5C", fontSize: "14.5px", lineHeight: 1.75 }}>{job.about}</p>

                <div className="row g-4 mb-4">
                  <div className="col-md-6">
                    <div className="small fw-bold text-uppercase mb-3" style={{ color: NAVY, letterSpacing: "0.05em" }}>
                      What You'll Do
                    </div>
                    <ul className="list-unstyled mb-0">
                      {responsibilities.map((item, i) => (
                        <li key={i} className="d-flex align-items-start mb-2">
                          <FiCheck size={15} style={{ color: GREEN, marginRight: 8, marginTop: 3, flexShrink: 0 }} />
                          <span className="small" style={{ color: "#5C5C5C" }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <div className="small fw-bold text-uppercase mb-3" style={{ color: NAVY, letterSpacing: "0.05em" }}>
                      What We're Looking For
                    </div>
                    <ul className="list-unstyled mb-0">
                      {requirements.map((item, i) => (
                        <li key={i} className="d-flex align-items-start mb-2">
                          <FiCheck size={15} style={{ color: GREEN, marginRight: 8, marginTop: 3, flexShrink: 0 }} />
                          <span className="small" style={{ color: "#5C5C5C" }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3 pt-3" style={{ borderTop: "1px solid #eef0ee" }}>
                  <a
                    href={mailtoHref}
                    className="btn rounded-pill px-4 py-2 fw-semibold small"
                    style={{ background: NAVY, color: "#fff" }}
                  >
                    Apply for this Role
                  </a>
                  <span className="small text-muted">Or email your resume directly to {APPLY_EMAIL}</span>
                </div>
              </div>
            );
          })}

          <div
            className="text-center shadow-sm mt-4"
            style={{ background: "#fff", border: "1px solid #eef0ee", borderRadius: 16, padding: "40px 24px" }}
          >
            <h6 className="fw-bold mb-2" style={{ color: NAVY }}>Don't see the right role?</h6>
            <p className="text-muted small mb-3">We'd still love to hear from you — tell us how you'd fit in.</p>
            <a
              href={`mailto:${APPLY_EMAIL}?subject=${encodeURIComponent("Interested in joining GuestLinkup")}`}
              className="btn rounded-pill px-4 py-2 fw-semibold small"
              style={{ background: NAVY, color: "#fff" }}
            >
              Get In Touch
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Careersection;
